import * as Sentry from '@sentry/react';
import { Route, Routes, Link } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import Gallery from './components/Gallery';
import Header from './components/Header';
import HomeOutreachesPanel from './components/HomeOutreachesPanel';
import ProtectedRoute from './components/ProtectedRoute';
import './components/PastEvents.css';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import PAST_EVENTS from './data/pastEvents';
import AboutPage from './pages/AboutPage';
import ApplicationPage from './pages/ApplicationPage';
import EventApplicationPage from './pages/EventApplicationPage';
import AuthPage from './pages/AuthPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountSetupPage from './pages/AccountSetupPage';
import DashboardPage from './pages/DashboardPage';
import ProfileCompletionPage from './pages/ProfileCompletionPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminEventFormPage from './pages/admin/AdminEventFormPage';
import AdminApplicationsPage from './pages/admin/AdminApplicationsPage';
import AdminApplicationsPrintPage from './pages/admin/AdminApplicationsPrintPage';
import AdminChurchesPage from './pages/admin/AdminChurchesPage';
import AdminEventGroupsPage from './pages/admin/AdminEventGroupsPage';
import AdminGroupDetailPage from './pages/admin/AdminGroupDetailPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import EventsOlympicsApply from './pages/events/OlympicsApplicationPage';
import EventsOlympicsDetails from './pages/events/OlympicsDetailsPage';
import EventsSuperBowlApply from './pages/events/SuperBowlApplicationPage';
import EventsSuperBowlDetails from './pages/events/SuperBowlDetailsPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import PastEventsPage from './pages/PastEventsPage';
import GalleryPage from './pages/GalleryPage';
import ShareYourFaithPage from './pages/LearnEvangelismPage';
import LinksPage from './pages/LinksPage';
import PinPage from './pages/PinPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import PreEventSurveyPage from './pages/PreEventSurveyPage';
import PostEventSurveyPage from './pages/PostEventSurveyPage';

function IndexPage() {
  console.log('[app] IndexPage render');
  return (
    <div className="App">
      <div className='container mt-4'>
        <h1 className="display-6 text-center mb-3">The Outreach Bible Project</h1>
        <p className="text-center text-muted mb-4">Taking the Word of God into a lost & dying world and empowering the average Christian to become an above average soul-winner.</p>
      </div>

      <HomeOutreachesPanel />

      <div className="container my-5">
        <div id="past-events-container">
          <h2 className="text-center mb-4">Past Events</h2>
          {PAST_EVENTS.map((ev, idx) => (
            <div key={ev.id} className={`past-event-row ${idx % 2 === 0 ? 'label-left' : 'label-right'}`}>
              <div className="past-event-label">{ev.title}</div>
              <Gallery
                images={ev.images}
                title=""
                initialLimit={3}
                showLoadMore={false}
                placeholderTextColor="#1e3a5f"
                counterTextColor="#1e3a5f"
              />
            </div>
          ))}
          <div className="text-center mt-4">
            <Link to="/past-events" className="btn btn-outline-primary">
              <i className="fas fa-history me-2" aria-hidden></i>
              View All Past Events
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  return (
    <ToastProvider>
    <AuthProvider>
      <Header />
      <main id="main" tabIndex={-1}>
      <SentryRoutes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/past-events" element={<PastEventsPage />} />
        <Route path="/application" element={<ApplicationPage />} />
        <Route path="/share-your-faith" element={<ShareYourFaithPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/fieldapp/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/fieldapp/delete-account" element={<DeleteAccountPage />} />
        <Route path="/links" element={<LinksPage />} />
        <Route path="/pin" element={<PinPage />} />

        {/* Authentication routes */}
        <Route path="/login" element={<AuthPage />} />
        <Route path="/auth" element={<AuthPage />} /> {/* Backwards compatibility redirect */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account-setup" element={<AccountSetupPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/complete"
          element={
            <ProtectedRoute>
              <ProfileCompletionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-applications"
          element={
            <ProtectedRoute>
              <MyApplicationsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminEventFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:eventId/applications"
          element={
            <ProtectedRoute requireAdmin>
              <AdminApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:eventId/groups"
          element={
            <ProtectedRoute requireAdmin>
              <AdminEventGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events/:eventId/groups/:groupId"
          element={
            <ProtectedRoute requireAdmin>
              <AdminGroupDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/churches"
          element={
            <ProtectedRoute requireAdmin>
              <AdminChurchesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/events/:eventId/applications/print"
          element={
            <ProtectedRoute requireAdmin>
              <AdminApplicationsPrintPage />
            </ProtectedRoute>
          }
        />

  {/* <Route path="/events/:slug/register" element={<ApplyPage />} /> */}
  {/* Canonical event info routes under /events/ */}
  <Route path="/events/olympics-2026" element={<EventsOlympicsDetails />} />
  <Route path="/events/super-bowl-2026" element={<EventsSuperBowlDetails />} />
  <Route path="/events/olympics-2026/apply" element={<EventsOlympicsApply />} />
  <Route path="/events/super-bowl-2026/apply" element={<EventsSuperBowlApply />} />
  <Route path="/pre-event-survey/:token" element={<PreEventSurveyPage />} />
  <Route path="/post-event-survey/:token" element={<PostEventSurveyPage />} />

  {/* Dynamic event application page - checks for legacy events */}
  <Route path="/events/:slug/apply" element={<EventApplicationPage />} />

  {/* Dynamic event detail page - must come after specific routes */}
  <Route path="/events/:slug" element={<EventDetailPage />} />
      </SentryRoutes>
      </main>
      <Footer />
    </AuthProvider>
    </ToastProvider>
  );
}

export default App;